import React from 'react';
import { Edit2 } from '@/components/icons';
import { HoverIntentGroup } from '@/components/ui/hover-intent-group';
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from '@/constants';
import { isChartSpecEmpty, resolveCanvasSize, resolveChartSpec } from '@/lib/chart-config';
import { explainEmptyChart, toNormalizedTableView } from '@/lib/table-chart';
import { ChartBlock, TableBlock, TestBlock } from '@/types/report-payload';
import PlaceholderBlock from '../wizard/test-blocks/PlaceholderBlock';
import { chartBoxStyle } from './chart-resize/chart-size';
import { ChartResizeHandles } from './chart-resize/ChartResizeHandles';
import { useChartResize } from './chart-resize/useChartResize';
import ChartRenderer from './ChartRenderer';
import { ReportData } from './types';

interface TestChartProps {
    block: ChartBlock;
    data: ReportData;
    disabled?: boolean;
    onEditChart: (block: TestBlock) => void;
    onBlockUpdate?: (blockId: string | number, updates: Partial<TestBlock>) => void;
    /**
     * Rendered inside the hidden measurer purely to size the block. The box
     * keeps its CSS-driven height, so we skip the live ECharts instance (which
     * only draws into the box, never determines its height) to avoid mounting a
     * second chart per block just for measurement.
     */
    isMeasuring?: boolean;
}

const TestChartComponent: React.FC<TestChartProps> = ({
    block,
    data,
    disabled = false,
    onEditChart,
    onBlockUpdate,
    isMeasuring = false,
}) => {
    const linkedTableBlock = React.useMemo(() => {
        if (!block.linked_table_id) return null;

        const sourceBlock = (data.tests?.blocks || []).find((candidate: TestBlock) => {
            return candidate.block_type === 'table' && candidate.id === block.linked_table_id;
        });

        return (sourceBlock as TableBlock | undefined) || null;
    }, [block.linked_table_id, data.tests?.blocks]);

    // Single shared resolution: preview and .docx build the identical spec.
    // The report's brand colors seed the default series colors (the user's
    // explicit per-series picks still win) so the editor matches the export.
    const primaryColor = data.customizations?.colors?.primary ?? DEFAULT_PRIMARY_COLOR;
    const secondaryColor = data.customizations?.colors?.secondary ?? DEFAULT_SECONDARY_COLOR;
    const spec = React.useMemo(() => {
        const table = linkedTableBlock ? toNormalizedTableView(linkedTableBlock) : null;
        return resolveChartSpec(block, { table, primaryColor, secondaryColor });
    }, [block, linkedTableBlock, primaryColor, secondaryColor]);

    const isEmpty = isChartSpecEmpty(spec);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const canvas = resolveCanvasSize(spec);
    const handleCommit = React.useCallback(
        (pct: number) => onBlockUpdate?.(block.id, { size: { width_pct: pct } }),
        [block.id, onBlockUpdate],
    );
    const { livePct, isResizing, handleResizeStart, didDragRef } = useChartResize({
        disabled: disabled || !onBlockUpdate,
        containerRef,
        storedPct: block.size?.width_pct,
        aspect: canvas.width / canvas.height,
        onCommit: handleCommit,
    });

    if (isEmpty) {
        // A configured chart that plotted nothing gets the specific reason;
        // an unconfigured one keeps the generic "configure me" copy.
        const emptyReason = block.linked_table_id
            ? explainEmptyChart(linkedTableBlock, block.source ?? {})
            : null;
        return (
            <PlaceholderBlock
                type="chart"
                label="Adicionar Gráfico"
                description={
                    emptyReason ??
                    (block.linked_table_id
                        ? 'Clique para escolher as colunas do gráfico desta tabela'
                        : 'Clique para configurar o gráfico')
                }
                onClick={() => !disabled && onEditChart(block)}
                disabled={disabled}
            />
        );
    }

    const box = chartBoxStyle(livePct ?? block.size?.width_pct, canvas);

    return (
        <HoverIntentGroup
            ref={containerRef}
            hoverIntent={{ disabled, keepOpen: isResizing }}
            className={`group/media relative ${box.className} ${disabled ? '' : 'cursor-pointer'}`}
            style={box.style}
            onClick={() => {
                if (didDragRef.current) {
                    didDragRef.current = false;
                    return;
                }
                if (!disabled) onEditChart(block);
            }}
        >
            <div className="pointer-events-none absolute inset-0 z-10 rounded-lg border-2 border-transparent group-data-[hover-intent=on]/media:border-blue-200" />
            {!disabled && !isResizing && (
                <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center opacity-0 transition-opacity duration-200 group-data-[hover-intent=on]/media:opacity-100">
                    <span className="flex items-center gap-1.5 rounded-full bg-slate-900/80 px-3 py-1.5 text-xs font-medium text-white shadow-sm">
                        <Edit2 className="size-3.5" />
                        Clique para editar
                    </span>
                </div>
            )}
            {!disabled && onBlockUpdate && (
                <ChartResizeHandles
                    isResizing={isResizing}
                    livePct={livePct}
                    onResizeStart={handleResizeStart}
                />
            )}
            {!isMeasuring && <ChartRenderer spec={spec} />}
        </HoverIntentGroup>
    );
};

/**
 * Only the chart block itself, the linked tables slice, and the brand colors
 * feed the rendered spec — so a report edit elsewhere (e.g. a narrative section)
 * must not re-run the expensive ECharts setOption. The callback props are
 * intentionally excluded: they close over stable setters, so a fresh identity
 * never changes behavior.
 */
const TestChart = React.memo(TestChartComponent, (prev, next) => {
    return (
        prev.block === next.block &&
        prev.disabled === next.disabled &&
        prev.isMeasuring === next.isMeasuring &&
        prev.data.tests?.blocks === next.data.tests?.blocks &&
        prev.data.customizations?.colors === next.data.customizations?.colors
    );
});

export default TestChart;
